import { Bundle, MeasureReport, Parameters } from "@medplum/fhirtypes";
import { populationCodeTooltips } from "../../../shared-components/fhir-sections/caregaps/shared";
import type { TableRow } from "../../utils/tables-metadata";
import { formatPeriod } from "../../utils/date-format-utils";
import {
  calculateCareGapStatus,
  calculateGroupStatus,
  formatPopulationCode,
  formatStatus,
  formatPascalCase,
  getStatusColor,
} from "./care-gaps-utils";

export type FormattedPopulation = {
  code: string;
  formattedCode: string;
  hasCount: boolean;
  tooltipText?: string;
};

export type FormattedGroup = {
  id: string;
  code: string;
  formattedCode: string;
  status: "completed" | "open" | "ineligible" | "excluded";
  formattedStatus: string;
  statusColor: string;
  populations: FormattedPopulation[];
};

export type CareGapRowData = TableRow & {
  measureUri: string;
  measureName: string;
  formattedMeasureName: string;
  status: "completed" | "open" | "ineligible" | "excluded";
  formattedStatus: string;
  statusColor: string;
  period: string;
  formattedGroups: FormattedGroup[];
  parameters: NonNullable<Parameters["parameter"]>;
};

function extractMeasureName(measureUrl: string): string {
  const parts = measureUrl.split("/");
  return parts[parts.length - 1] ?? measureUrl;
}

function findEntry<T extends { resourceType: string }>(
  bundle: Bundle,
  resourceType: string
): T | undefined {
  return bundle.entry
    ?.map(e => e.resource)
    .find(r => r && (r as { resourceType?: string }).resourceType === resourceType) as
    | T
    | undefined;
}

export function getCareGapRows({ bundles }: { bundles?: Bundle[] }): CareGapRowData[] {
  return (
    bundles?.flatMap(bundle => {
      const measureReport = findEntry<MeasureReport>(bundle, "MeasureReport");
      if (!measureReport) return [];

      const parameters = findEntry<Parameters>(bundle, "Parameters");
      const start = measureReport.period?.start ?? "";
      const end = measureReport.period?.end ?? "";
      const periodFormatted = formatPeriod(start, end);

      const measureUri = measureReport.measure ?? "";
      const rawName = extractMeasureName(measureUri);
      const firstGroupCode = measureReport.group?.[0]?.code?.coding?.[0]?.code || rawName;
      const formattedMeasureNameValue = formatPascalCase(firstGroupCode);

      const calculatedStatus = calculateCareGapStatus(measureReport);

      const formattedGroupsData: FormattedGroup[] =
        measureReport.group?.map((group, groupIndex) => {
          const groupCode = group.code?.coding?.[0]?.code || group.id || `Group ${groupIndex + 1}`;
          const groupStatus = calculateGroupStatus(group);

          const formattedPopulations: FormattedPopulation[] =
            group.population?.map((pop, popIndex) => {
              const popCode = pop.code?.coding?.[0]?.code || `Population ${popIndex + 1}`;
              const hasCount = pop.count !== undefined && pop.count > 0;
              const formattedPopCode = formatPopulationCode(popCode);
              const tooltipText =
                populationCodeTooltips[
                  popCode.toLowerCase() as keyof typeof populationCodeTooltips
                ];

              return { code: popCode, formattedCode: formattedPopCode, hasCount, tooltipText };
            }) ?? [];

          return {
            id: group.id ?? `group-${groupIndex}`,
            code: groupCode,
            formattedCode: groupCode,
            status: groupStatus,
            formattedStatus: formatStatus(groupStatus),
            statusColor: getStatusColor(groupStatus),
            populations: formattedPopulations,
          };
        }) ?? [];

      return [
        {
          id: measureReport.id ?? bundle.id ?? "",
          measureUri,
          measureName: rawName,
          formattedMeasureName: formattedMeasureNameValue,
          status: calculatedStatus,
          formattedStatus: formatStatus(calculatedStatus),
          statusColor: getStatusColor(calculatedStatus),
          period: periodFormatted,
          formattedGroups: formattedGroupsData,
          parameters: parameters?.parameter ?? [],
        },
      ];
    }) ?? []
  );
}
